import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import Loadable from "../layouts/full/shared/loadable/Loadable";
import { RouterName } from "./RouterName";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import NotificationController from "src/pages/notificaiton/view";
import Events from "src/pages/events/view";
import Intro from "src/pages/intro/view";
import MediaList from "src/pages/media_list/view";
import MediaTypes from "src/pages/media_types/view";
import EventsMediaGallery from "src/pages/event_media_gallery/view";
import MediaListMediaGallery from "src/pages/media_list_media_gallery/view";
import Locations from "src/pages/locations/view";
import Courses from "src/pages/courses/view";
import CourseFiles from "src/pages/course-files/view";
import ClientOrders from "src/pages/client-orders/view";
import WebhookLocks from "src/pages/webhook_locks/view";
import Cbt from "src/pages/cbt/view";
import Values from "src/pages/values/view";
import WhyUs from "src/pages/why_us/view";
import Testimonial from "src/pages/testimonials/view";
import Team from "src/pages/team/view";
import Slider from "src/pages/slider/view";
import MainServices from "src/pages/main_services/view";
import SubServices from "src/pages/sub_services/view";
import SubServicesMediaGallery from "src/pages/subservices_media_gallery/view";
import TrainingWorkshops from "src/pages/training_workshops/view";
import Category from "src/pages/category/view";
import JoinOurTeam from "src/pages/join_our_team/view";
import AboutUsMediaGallery from "src/pages/about_us_media_gallery/view";
import ClinicMediaGallery from "src/pages/clinic_media_gallery/view";

// Helper function to get the first accessible route based on user privileges
const getDefaultRoute = () => {
  const data = getLocalStorage(dataLocalStorage.privileges);

  // Check each route in order of priority (matching MenuItems.js order)
  if (data["15"]?.access_read == 1) return RouterName.events.events;
  if (data["16"]?.access_read == 1) return RouterName.intro.intro;
  if (data["31"]?.access_read == 1) return RouterName.join_our_team;
  if (data["17"]?.access_read == 1) return RouterName.medialist.medialist;
  if (data["18"]?.access_read == 1) return RouterName.mediatypes.mediatypes;
  if (data["19"]?.access_read == 1) return RouterName.locations.locations;
  if (data["20"]?.access_read == 1) return RouterName.courses;
  if (data["21"]?.access_read == 1) return RouterName.clientOrders;
  if (data["22"]?.access_read == 1) return RouterName.values;
  if (data["23"]?.access_read == 1) return RouterName.whyUs;
  if (data["24"]?.access_read == 1) return RouterName.testimonial;
  if (data["25"]?.access_read == 1) return RouterName.team;
  if (data["26"]?.access_read == 1) return RouterName.slider;
  if (data["27"]?.access_read == 1) return RouterName.main_services;
  if (data["28"]?.access_read == 1) return RouterName.sub_services;
  if (data["32"]?.access_read == 1) return RouterName.training_workshops;
  if (data["29"]?.access_read == 1) return RouterName.category.category;
  if (data["30"]?.access_read == 1) return RouterName.seo;

  // Security section
  if (data["1"]?.access_read == 1) return RouterName.admins.admins;
  if (data["5"]?.access_read == 1) return RouterName.settings.settings;
  if (data["12"]?.access_read == 1) return RouterName.admins_logs.admins_logs;

  // If no privileges found, default to intro page as last resort
  return RouterName.intro.intro;
};

/* ***Layouts**** */
const FullLayout = Loadable(lazy(() => import("../layouts/full/FullLayout")));
const BlankLayout = Loadable(
  lazy(() => import("../layouts/blank/BlankLayout"))
);

/* ****Pages***** */
const Tips = Loadable(lazy(() => import("src/pages/tips/view")));
const AdminLogs = Loadable(lazy(() => import("src/pages/admins_logs/view")));

const Users = Loadable(lazy(() => import("src/pages/users/view")));

const GeneralHomePage = Loadable(
  lazy(() => import("src/pages/landingPage/generalHomePage/view"))
);
const OurStory = Loadable(
  lazy(() => import("src/pages/landingPage/ourStory/view"))
);

const OurTeam = Loadable(
  lazy(() => import("src/pages/landingPage/ourTeam/view"))
);
const FlashcardDetailsPage = Loadable(
  lazy(() => import("src/pages/flashCard/Details/view"))
);
const FlashCardCategory = Loadable(
  lazy(() => import("src/pages/flashCard/category/view"))
);
const FlashCard = Loadable(
  lazy(() => import("src/pages/flashCard/flashCard/view"))
);
const LevelOfDifficulty = Loadable(
  lazy(() => import("src/pages/flashCard/levelOfDifficulty/view"))
);
const AgeRange = Loadable(
  lazy(() => import("src/pages/flashCard/ageRange/view"))
);
const HomePage = Loadable(lazy(() => import("src/pages/home/view")));
const Faqs = Loadable(lazy(() => import("src/pages/faqs/view")));
const PressBlogNews = Loadable(
  lazy(() => import("src/pages/landingPage/press_blog_news/view"))
);

const CategoryActivities = Loadable(
  lazy(() => import("src/pages/activities/category/view"))
);
const Activities = Loadable(
  lazy(() => import("src/pages/activities/activities/view"))
);
const ActivitiesMedia = Loadable(
  lazy(() => import("src/pages/activities/activities_media/view"))
);
const ActivitiesFlashcard = Loadable(
  lazy(() => import("src/pages/activities/activites_flashcard/view"))
);
const Settings = Loadable(lazy(() => import("src/pages/settings/view")));
const LegalInformation = Loadable(
  lazy(() => import("src/pages/settings/legal_information"))
);
const ContactAs = Loadable(lazy(() => import("src/pages/contact_as/view")));

const HowItWork = Loadable(lazy(() => import("src/pages/how_it_work/view")));
const HowItWorkDetails = Loadable(
  lazy(() => import("src/pages/how_it_work/details"))
);

const OurClient = Loadable(
  lazy(() => import("src/pages/landingPage/ourClient/view"))
);
const Testimonials = Loadable(
  lazy(() => import("src/pages/landingPage/testimonials/view"))
);
const AppBenefits = Loadable(
  lazy(() => import("src/pages/landingPage/app_benefits/view"))
);
const Impacts = Loadable(
  lazy(() => import("src/pages/landingPage/impacts/view"))
);
const Stats = Loadable(lazy(() => import("src/pages/landingPage/stats/view")));

const Topics = Loadable(lazy(() => import("src/pages/topics/view")));
const SubTopics = Loadable(lazy(() => import("src/pages/topics/subtopic")));

const EcommerceDash = Loadable(
  lazy(() => import("../views/dashboard/Ecommerce"))
);

const AdminsPage = Loadable(lazy(() => import("../pages/admins/admins/view")));
const PrivilegesPage = Loadable(
  lazy(() => import("../pages/admins/privileges/view"))
);

const SliderShowPage = Loadable(
  lazy(() => import("../pages/landingPage/slidershow/view"))
);
const MapsPage = Loadable(lazy(() => import("../pages/landingPage/maps/view")));
const LanguagesPage = Loadable(
  lazy(() => import("../pages/landingPage/languages/view"))
);
/* ****Apps***** */
const Chats = Loadable(lazy(() => import("../views/apps/chat/Chat")));
const Notes = Loadable(lazy(() => import("../views/apps/notes/Notes")));
const Calendar = Loadable(
  lazy(() => import("../views/apps/calendar/BigCalendar"))
);
const Email = Loadable(lazy(() => import("../views/apps/email/Email")));
const Blog = Loadable(lazy(() => import("../views/apps/blog/Blog")));
const BlogDetail = Loadable(lazy(() => import("../views/apps/blog/BlogPost")));
const Tickets = Loadable(lazy(() => import("../views/apps/tickets/Tickets")));
const Contacts = Loadable(
  lazy(() => import("../views/apps/contacts/Contacts"))
);
const Ecommerce = Loadable(
  lazy(() => import("../views/apps/eCommerce/Ecommerce"))
);
const EcommerceDetail = Loadable(
  lazy(() => import("../views/apps/eCommerce/EcommerceDetail"))
);
const EcomProductList = Loadable(
  lazy(() => import("../views/apps/eCommerce/EcomProductList"))
);
const EcomProductCheckout = Loadable(
  lazy(() => import("../views/apps/eCommerce/EcommerceCheckout"))
);
const UserProfile = Loadable(
  lazy(() => import("../views/apps/user-profile/UserProfile"))
);
const Followers = Loadable(
  lazy(() => import("../views/apps/user-profile/Followers"))
);
const Friends = Loadable(
  lazy(() => import("../views/apps/user-profile/Friends"))
);
const Gallery = Loadable(
  lazy(() => import("../views/apps/user-profile/Gallery"))
);

// Pages
const RollbaseCASL = Loadable(
  lazy(() => import("../views/pages/rollbaseCASL/RollbaseCASL"))
);
const Treeview = Loadable(
  lazy(() => import("../views/pages/treeview/Treeview"))
);
const Pricing = Loadable(lazy(() => import("../views/pages/pricing/Pricing")));
const AccountSetting = Loadable(
  lazy(() => import("../views/pages/account-setting/AccountSetting"))
);
const Faq = Loadable(lazy(() => import("../views/pages/faq/Faq")));

// widget
const WidgetCards = Loadable(
  lazy(() => import("../views/widgets/cards/WidgetCards"))
);
const WidgetBanners = Loadable(
  lazy(() => import("../views/widgets/banners/WidgetBanners"))
);
const WidgetCharts = Loadable(
  lazy(() => import("../views/widgets/charts/WidgetCharts"))
);

// form elements
const MuiAutoComplete = Loadable(
  lazy(() => import("../views/forms/form-elements/MuiAutoComplete"))
);
const MuiButton = Loadable(
  lazy(() => import("../views/forms/form-elements/MuiButton"))
);
const MuiCheckbox = Loadable(
  lazy(() => import("../views/forms/form-elements/MuiCheckbox"))
);
const MuiRadio = Loadable(
  lazy(() => import("../views/forms/form-elements/MuiRadio"))
);
const MuiSlider = Loadable(
  lazy(() => import("../views/forms/form-elements/MuiSlider"))
);
const MuiDateTime = Loadable(
  lazy(() => import("../views/forms/form-elements/MuiDateTime"))
);
const MuiSwitch = Loadable(
  lazy(() => import("../views/forms/form-elements/MuiSwitch"))
);

// form layout
const FormLayouts = Loadable(lazy(() => import("../views/forms/FormLayouts")));
const FormCustom = Loadable(lazy(() => import("../views/forms/FormCustom")));
const FormWizard = Loadable(lazy(() => import("../views/forms/FormWizard")));
const FormValidation = Loadable(
  lazy(() => import("../views/forms/FormValidation"))
);
const QuillEditor = Loadable(
  lazy(() => import("../views/forms/quill-editor/QuillEditor"))
);
const FormHorizontal = Loadable(
  lazy(() => import("../views/forms/FormHorizontal"))
);
const FormVertical = Loadable(
  lazy(() => import("../views/forms/FormVertical"))
);

// tables
const BasicTable = Loadable(lazy(() => import("../views/tables/BasicTable")));
const CollapsibleTable = Loadable(
  lazy(() => import("../views/tables/CollapsibleTable"))
);
const EnhancedTable = Loadable(
  lazy(() => import("../views/tables/EnhancedTable"))
);
const FixedHeaderTable = Loadable(
  lazy(() => import("../views/tables/FixedHeaderTable"))
);
const PaginationTable = Loadable(
  lazy(() => import("../views/tables/PaginationTable"))
);
const SearchTable = Loadable(lazy(() => import("../views/tables/SearchTable")));

// chart
const LineChart = Loadable(lazy(() => import("../views/charts/LineChart")));
const GredientChart = Loadable(
  lazy(() => import("../views/charts/GredientChart"))
);
const DoughnutChart = Loadable(
  lazy(() => import("../views/charts/DoughnutChart"))
);
const AreaChart = Loadable(lazy(() => import("../views/charts/AreaChart")));
const ColumnChart = Loadable(lazy(() => import("../views/charts/ColumnChart")));
const CandlestickChart = Loadable(
  lazy(() => import("../views/charts/CandlestickChart"))
);
const RadialbarChart = Loadable(
  lazy(() => import("../views/charts/RadialbarChart"))
);

// ui
const MuiAlert = Loadable(
  lazy(() => import("../views/ui-components/MuiAlert"))
);
const MuiAccordion = Loadable(
  lazy(() => import("../views/ui-components/MuiAccordion"))
);
const MuiAvatar = Loadable(
  lazy(() => import("../views/ui-components/MuiAvatar"))
);
const MuiChip = Loadable(lazy(() => import("../views/ui-components/MuiChip")));
const MuiDialog = Loadable(
  lazy(() => import("../views/ui-components/MuiDialog"))
);
const MuiList = Loadable(lazy(() => import("../views/ui-components/MuiList")));
const MuiPopover = Loadable(
  lazy(() => import("../views/ui-components/MuiPopover"))
);
const MuiRating = Loadable(
  lazy(() => import("../views/ui-components/MuiRating"))
);
const MuiTabs = Loadable(lazy(() => import("../views/ui-components/MuiTabs")));
const MuiTooltip = Loadable(
  lazy(() => import("../views/ui-components/MuiTooltip"))
);
const MuiTransferList = Loadable(
  lazy(() => import("../views/ui-components/MuiTransferList"))
);
const MuiTypography = Loadable(
  lazy(() => import("../views/ui-components/MuiTypography"))
);

// authentication
const Login = Loadable(
  lazy(() => import("../views/authentication/auth/Login"))
);

const ForgotPassword = Loadable(
  lazy(() => import("../views/authentication/auth/ForgotPassword"))
);
const ResetPassword = Loadable(
  lazy(() => import("src/views/authentication/auth/ResetPassword"))
);

const Error = Loadable(lazy(() => import("../views/authentication/Error")));
const Maintenance = Loadable(
  lazy(() => import("../views/authentication/Maintenance"))
);
const Seo = Loadable(lazy(() => import("src/pages/seo/view")));

const Router = [
  {
    path: "/",
    element: <FullLayout />,
    children: [
      {
        path: "/",
        exact: true,
        element: <Navigate to={getDefaultRoute()} />,
      },
      {
        path: RouterName.home.home,
        exact: true,
        element: <HomePage />,
      },
      {
        path: RouterName.home.home,
        exact: true,
        element: <HomePage />,
      },
      {
        path: RouterName.admins.admins,
        exact: true,
        element: <AdminsPage />,
      },
      {
        path: RouterName.users.users,
        exact: true,
        element: <Users />,
      },
      {
        path: RouterName.admins.privileges,
        exact: true,
        element: <PrivilegesPage />,
      },
      {
        path: RouterName.flashcard.flashcard,
        exact: true,
        element: <FlashCard />,
      },
      {
        path: RouterName.flashcard.flashcardDetails,
        exact: true,
        element: <FlashcardDetailsPage />,
      },
      {
        path: RouterName.flashcard.age_range,
        exact: true,
        element: <AgeRange />,
      },
      {
        path: RouterName.flashcard.level_of_difficulty,
        exact: true,
        element: <LevelOfDifficulty />,
      },

      {
        path: RouterName.landing_page.slidershow,
        exact: true,
        element: <SliderShowPage />,
      },
      {
        path: RouterName.landing_page.maps,
        exact: true,
        element: <MapsPage />,
      },

      {
        path: RouterName.landing_page.our_client,
        exact: true,
        element: <OurClient />,
      },
      {
        path: RouterName.landing_page.GeneralHomePage,
        exact: true,
        element: <GeneralHomePage />,
      },
      {
        path: RouterName.landing_page.ourStory,
        exact: true,
        element: <OurStory />,
      },
      {
        path: RouterName.landing_page.languages,
        exact: true,
        element: <LanguagesPage />,
      },
      {
        path: RouterName.landing_page.testimonials,
        exact: true,
        element: <Testimonials />,
      },
      {
        path: RouterName.landing_page.app_benefits,
        exact: true,
        element: <AppBenefits />,
      },
      {
        path: RouterName.landing_page.impacts,
        exact: true,
        element: <Impacts />,
      },
      {
        path: RouterName.landing_page.stats,
        exact: true,
        element: <Stats />,
      },
      {
        path: RouterName.landing_page.press_blog_news,
        exact: true,
        element: <PressBlogNews />,
      },
      {
        path: RouterName.landing_page.our_team,
        exact: true,
        element: <OurTeam />,
      },
      {
        path: RouterName.settings.settings,
        exact: true,
        element: <Settings />,
      },
      {
        path: RouterName.settings.legal_information,
        exact: true,
        element: <LegalInformation />,
      },
      {
        path: RouterName.how_it_work.how_it_work,
        exact: true,
        element: <HowItWork />,
      },
      {
        path: RouterName.how_it_work.how_it_work_details,
        exact: true,
        element: <HowItWorkDetails />,
      },

      {
        path: RouterName.activities.category,
        exact: true,
        element: <CategoryActivities />,
      },
      {
        path: RouterName.activities.activities,
        exact: true,
        element: <Activities />,
      },
      {
        path: RouterName.activities.activities_media,
        exact: true,
        element: <ActivitiesMedia />,
      },
      {
        path: RouterName.activities.activities_flashcard,
        exact: true,
        element: <ActivitiesFlashcard />,
      },
      {
        path: RouterName.topics.topics,
        exact: true,
        element: <Topics />,
      },
      {
        path: RouterName.topics.SubTopics,
        exact: true,
        element: <SubTopics />,
      },
      {
        path: RouterName.ContactAs.ContactAs,
        exact: true,
        element: <ContactAs />,
      },
      {
        path: RouterName.faqs.faqs,
        exact: true,
        element: <Faqs />,
      },
      {
        path: RouterName.tips.tips,
        exact: true,
        element: <Tips />,
      },
      {
        path: RouterName.admins_logs.admins_logs,
        exact: true,
        element: <AdminLogs />,
      },
      {
        path: RouterName.notification.notification,
        exact: true,
        element: <NotificationController />,
      },
      {
        path: RouterName.events.events,
        exact: true,
        element: <Events />,
      },
      {
        path: RouterName.intro.intro,
        exact: true,
        element: <Intro />,
      },
      {
        path: RouterName.medialist.medialist,
        exact: true,
        element: <MediaList />,
      },
      {
        path: RouterName.mediatypes.mediatypes,
        exact: true,
        element: <MediaTypes />,
      },
      {
        path: RouterName.category.category,
        exact: true,
        element: <Category />,
      },
      {
        path: RouterName.eventsmediagallery.eventsmediagallery,
        exact: true,
        element: <EventsMediaGallery />,
      },
      {
        path: RouterName.courseFiles,
        exact: true,
        element: <CourseFiles />,
      },
      {
        path: RouterName.medialistmediagallery.medialistmediagallery,
        exact: true,
        element: <MediaListMediaGallery />,
      },
      {
        path: RouterName.intro.intro,
        exact: true,
        element: <Intro />,
      },
      {
        path: RouterName.locations.locations,
        exact: true,
        element: <Locations />,
      },
      {
        path: RouterName.courses,
        exact: true,
        element: <Courses />,
      },
      {
        path: RouterName.clientOrders,
        exact: true,
        element: <ClientOrders />,
      },
      {
        path: RouterName.webhook_locks,
        exact: true,
        element: <WebhookLocks />,
      },
      {
        path: RouterName.values,
        exact: true,
        element: <Values />,
      },
      {
        path: RouterName.whyUs,
        exact: true,
        element: <WhyUs />,
      },
      {
        path: RouterName.testimonial,
        exact: true,
        element: <Testimonial />,
      },
      {
        path: RouterName.team,
        exact: true,
        element: <Team />,
      },
      {
        path: RouterName.slider,
        exact: true,
        element: <Slider />,
      },
      {
        path: RouterName.main_services,
        exact: true,
        element: <MainServices />,
      },
      {
        path: RouterName.sub_services,
        exact: true,
        element: <SubServices />,
      },
      {
        path: RouterName.sub_services_media_gallery,
        exact: true,
        element: <SubServicesMediaGallery />,
      },
      {
        path: RouterName.training_workshops,
        exact: true,
        element: <TrainingWorkshops />,
      },
      {
        path: RouterName.join_our_team,
        exact: true,
        element: <JoinOurTeam />,
      },
      {
        path: RouterName.cbt,
        exact: true,
        element: <Cbt />,
      },
      {
        path: RouterName.about_us_media_gallery,
        exact: true,
        element: <AboutUsMediaGallery />,
      },
      {
        path: RouterName.clinic_media_gallery,
        exact: true,
        element: <ClinicMediaGallery />,
      },
      {
        path: RouterName.seo,
        exact: true,
        element: <Seo />,
      },
      // -----------------------------------------------------------------------------
      {
        path: "/dashboards/ecommerce",
        exact: true,
        element: <EcommerceDash />,
      },
      { path: "/apps/chats", element: <Chats /> },
      { path: "/apps/notes", element: <Notes /> },
      { path: "/apps/calendar", element: <Calendar /> },
      { path: "/apps/email", element: <Email /> },
      { path: "/apps/tickets", element: <Tickets /> },
      { path: "/apps/contacts", element: <Contacts /> },
      { path: "/apps/ecommerce/shop", element: <Ecommerce /> },
      { path: "/apps/blog/posts", element: <Blog /> },
      { path: "/apps/blog/detail/:id", element: <BlogDetail /> },
      {
        path: "/apps/ecommerce/eco-product-list",
        element: <EcomProductList />,
      },
      {
        path: "/apps/ecommerce/eco-checkout",
        element: <EcomProductCheckout />,
      },
      { path: "/apps/ecommerce/detail/:id", element: <EcommerceDetail /> },
      { path: "/apps/followers", element: <Followers /> },
      { path: "/apps/friends", element: <Friends /> },
      { path: "/apps/gallery", element: <Gallery /> },
      { path: "/user-profile", element: <UserProfile /> },
      { path: "/pages/casl", element: <RollbaseCASL /> },
      { path: "/pages/treeview", element: <Treeview /> },
      { path: "/pages/pricing", element: <Pricing /> },
      { path: "/pages/account-settings", element: <AccountSetting /> },
      { path: "/pages/faq", element: <Faq /> },
      {
        path: "/forms/form-elements/autocomplete",
        element: <MuiAutoComplete />,
      },
      { path: "/forms/form-elements/button", element: <MuiButton /> },
      { path: "/forms/form-elements/checkbox", element: <MuiCheckbox /> },
      { path: "/forms/form-elements/radio", element: <MuiRadio /> },
      { path: "/forms/form-elements/slider", element: <MuiSlider /> },
      { path: "/forms/form-elements/date-time", element: <MuiDateTime /> },
      { path: "/forms/form-elements/switch", element: <MuiSwitch /> },
      { path: "/forms/form-elements/switch", element: <MuiSwitch /> },
      { path: "/forms/quill-editor", element: <QuillEditor /> },
      { path: "/forms/form-layouts", element: <FormLayouts /> },
      { path: "/forms/form-horizontal", element: <FormHorizontal /> },
      { path: "/forms/form-vertical", element: <FormVertical /> },
      { path: "/forms/form-custom", element: <FormCustom /> },
      { path: "/forms/form-wizard", element: <FormWizard /> },
      { path: "/forms/form-validation", element: <FormValidation /> },
      { path: "/tables/basic", element: <BasicTable /> },
      { path: "/tables/collapsible", element: <CollapsibleTable /> },
      { path: "/tables/enhanced", element: <EnhancedTable /> },
      { path: "/tables/fixed-header", element: <FixedHeaderTable /> },
      { path: "/tables/pagination", element: <PaginationTable /> },
      { path: "/tables/search", element: <SearchTable /> },
      { path: "/charts/line-chart", element: <LineChart /> },
      { path: "/charts/gredient-chart", element: <GredientChart /> },
      { path: "/charts/doughnut-pie-chart", element: <DoughnutChart /> },
      { path: "/charts/area-chart", element: <AreaChart /> },
      { path: "/charts/column-chart", element: <ColumnChart /> },
      { path: "/charts/candlestick-chart", element: <CandlestickChart /> },
      { path: "/charts/radialbar-chart", element: <RadialbarChart /> },
      { path: "/ui-components/alert", element: <MuiAlert /> },
      { path: "/ui-components/accordion", element: <MuiAccordion /> },
      { path: "/ui-components/avatar", element: <MuiAvatar /> },
      { path: "/ui-components/chip", element: <MuiChip /> },
      { path: "/ui-components/dialog", element: <MuiDialog /> },
      { path: "/ui-components/list", element: <MuiList /> },
      { path: "/ui-components/popover", element: <MuiPopover /> },
      { path: "/ui-components/rating", element: <MuiRating /> },
      { path: "/ui-components/tabs", element: <MuiTabs /> },
      { path: "/ui-components/tooltip", element: <MuiTooltip /> },
      { path: "/ui-components/transfer-list", element: <MuiTransferList /> },
      { path: "/ui-components/typography", element: <MuiTypography /> },
      { path: "/widgets/cards", element: <WidgetCards /> },
      { path: "/widgets/banners", element: <WidgetBanners /> },
      { path: "/widgets/charts", element: <WidgetCharts /> },
      { path: "/404", element: <Error /> },
      { path: "*", element: <Navigate to="/" /> },
    ],
  },
  {
    path: "/",
    element: <BlankLayout />,
    children: [
      { path: "/auth/404", element: <Error /> },
      { path: RouterName.auth.login, element: <Login /> },
      { path: RouterName.auth.forgotPassword, element: <ForgotPassword /> },
      { path: RouterName.auth.resetPassword, element: <ResetPassword /> },
      { path: RouterName.auth.maintenance, element: <Maintenance /> },
      { path: "*", element: <Navigate to="/auth/404" /> },
    ],
  },
];

export default Router;
